import { cartDebug } from '@/scripts/cart/optimistic-debug'

type ReploAtcPayload = {
	variantId: number
	quantity: number
	sellingPlanId?: number
	payLater?: string
	checkoutMode: 'cart' | 'checkout'
}

const SELECTORS = {
	productContainer: '[data-replo-product-container]',
	form: 'form[data-type="add-to-cart-form"]',
	spinner: '.replo-animate-spinner',
}

const CHECKOUT_PAY_LATER_METHODS = new Set(['afterpay', 'paypal'])

function parsePositiveInt(value: string | null | undefined): number | null {
	const parsed = Number.parseInt(value ?? '', 10)
	if (!Number.isSafeInteger(parsed) || parsed <= 0) return null
	return parsed
}

function normalizePayLater(
	value: string | null | undefined
): string | undefined {
	const normalized = (value ?? '').trim().toLowerCase()
	return normalized || undefined
}

function resolveCheckoutMode(params: {
	trigger: HTMLElement
	container: HTMLElement
}): 'cart' | 'checkout' {
	const { trigger, container } = params

	const mode = (
		trigger.dataset.reploCheckoutMode ??
		container.dataset.reploCheckoutMode ??
		''
	)
		.trim()
		.toLowerCase()

	if (mode === 'checkout') return 'checkout'
	if (mode === 'cart') return 'cart'

	if (
		trigger.hasAttribute('data-redirect-to-checkout') ||
		container.hasAttribute('data-redirect-to-checkout')
	) {
		return 'checkout'
	}

	return 'cart'
}

function resolveVariantId(params: {
	form: HTMLFormElement
	trigger: HTMLElement
	container: HTMLElement
}): number | null {
	const { form, trigger, container } = params

	const variantInput =
		form.querySelector<HTMLInputElement>('input[name="id"]')
	const fromForm = parsePositiveInt(variantInput?.value)
	if (fromForm) return fromForm

	const triggerVariantEl =
		trigger.closest<HTMLElement>('[data-variant-id]') ??
		trigger.querySelector<HTMLElement>('[data-variant-id]')
	const fromTrigger = parsePositiveInt(
		triggerVariantEl?.getAttribute('data-variant-id')
	)
	if (fromTrigger) return fromTrigger

	const containerVariantEl =
		container.querySelector<HTMLElement>(
			'[data-replo-price][data-variant-id]'
		) ?? container.querySelector<HTMLElement>('[data-variant-id]')
	const fromContainer = parsePositiveInt(
		containerVariantEl?.getAttribute('data-variant-id')
	)
	if (fromContainer) return fromContainer

	return null
}

function parsePayload(params: {
	form: HTMLFormElement
	trigger: HTMLElement
	container: HTMLElement
}): ReploAtcPayload | null {
	const { form, trigger, container } = params
	const quantityInput = form.querySelector<HTMLInputElement>(
		'input[name="quantity"]'
	)
	const sellingPlanInput = form.querySelector<HTMLInputElement>(
		'input[name="selling_plan"]'
	)
	const payLaterInput = form.querySelector<HTMLInputElement>(
		'input[name="_pay_later"]'
	)

	const variantId = resolveVariantId({ form, trigger, container })
	if (!variantId) return null

	const quantity = parsePositiveInt(quantityInput?.value) ?? 1
	const sellingPlanId = parsePositiveInt(sellingPlanInput?.value)
	const payLater =
		normalizePayLater(payLaterInput?.value) ??
		normalizePayLater(trigger.dataset.expressPayment)
	const checkoutMode = resolveCheckoutMode({ trigger, container })

	return {
		variantId,
		quantity,
		sellingPlanId: sellingPlanId ?? undefined,
		payLater,
		checkoutMode,
	}
}

function isReploButton(element: Element | null): element is HTMLElement {
	if (!element) return false
	if (!element.closest(SELECTORS.productContainer)) return false
	return Boolean(element.querySelector(SELECTORS.spinner))
}

export function initReploAtcBridge(): void {
	document.addEventListener(
		'click',
		(event) => {
			const target = event.target as Element | null
			const trigger = target?.closest<HTMLElement>(
				'button, [role="button"]'
			)
			if (!isReploButton(trigger ?? null)) return
			if (!trigger) return

			const add = window.pvolve?.cart?.add
			const checkout = window.pvolve?.cart?.checkout
			if (!add && !checkout) return

			const container = trigger.closest<HTMLElement>(
				SELECTORS.productContainer
			)
			if (!container) return

			const form = container.querySelector<HTMLFormElement>(
				SELECTORS.form
			)
			if (!form) return

			const payload = parsePayload({
				form,
				trigger,
				container,
			})
			if (!payload) return

			if (trigger.dataset.reploAtcPending === 'true') {
				event.preventDefault()
				event.stopPropagation()
				return
			}

			event.preventDefault()
			event.stopPropagation()
			event.stopImmediatePropagation()

			trigger.dataset.reploAtcPending = 'true'
			trigger.setAttribute('aria-disabled', 'true')
			const clearPendingState = () => {
				trigger.removeAttribute('aria-disabled')
				delete trigger.dataset.reploAtcPending
			}
			cartDebug('replo-atc-bridge:add:start', {
				variantId: payload.variantId,
				quantity: payload.quantity,
				sellingPlanId: payload.sellingPlanId ?? null,
				payLater: payload.payLater ?? null,
				checkoutMode: payload.checkoutMode,
			})

			const shouldCheckout =
				payload.checkoutMode === 'checkout' &&
				payload.payLater !== undefined &&
				CHECKOUT_PAY_LATER_METHODS.has(payload.payLater) &&
				typeof checkout === 'function'

			if (!shouldCheckout && typeof add !== 'function') {
				clearPendingState()
				return
			}

			const request = shouldCheckout
				? checkout(payload.variantId, {
						quantity: payload.quantity,
						sellingPlanId: payload.sellingPlanId,
						payLater: payload.payLater as string,
					})
				: add(payload.variantId, {
						quantity: payload.quantity,
						sellingPlanId: payload.sellingPlanId,
						payLater: payload.payLater,
						openCart: true,
					})

			void request
				.then((result) => {
					cartDebug('replo-atc-bridge:add:result', {
						success: result.success,
						error: result.error ?? null,
					})
				})
				.catch((error) => {
					cartDebug('replo-atc-bridge:add:error', {
						error:
							error instanceof Error
								? error.message
								: String(error),
					})
				})
				.finally(() => {
					clearPendingState()
				})
		},
		true
	)
}
